import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Components from '@u4/next-components';
import { RootState } from '../../app/store';
import { useApi } from '../../app/apiClient';
import { useAccessToken } from '../../app/authorization';
import {
  loadLastDiagnosticAsync,
  runDiagnosticsAsync,
  DiagnosticsState
} from './diagnosticSlice';
import { DiagnosticStatuses } from '../../app/dto/diagnosticResultDto';
import mapToDiagnosticTranslations, { getDiagnosticErrorMessage } from '../../app/mappers/mapToDiagnosticTranslations';
import './diagnosticStyles.css';

function formatTimestamp(iso?: string | null): string {
  if (!iso) {
    return '';
  }
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export const Diagnostic: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const token = useAccessToken();
  const apiClient = useApi();
  const translations = useMemo(() => mapToDiagnosticTranslations(), []);

  const { isRunning, isLoading, result, error } = useSelector(
    (state: RootState): DiagnosticsState => state.diagnostic
  );

  useEffect(() => {
    if (token) {
      dispatch(loadLastDiagnosticAsync(apiClient, translations));
    }
  }, [dispatch, apiClient, token]);

  const runAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      runAbortRef.current?.abort();
    };
  }, []);

  function handleRun() {
    if (isRunning || isPending) {
      return;
    }

    runAbortRef.current?.abort();
    const controller = new AbortController();
    runAbortRef.current = controller;
    dispatch(runDiagnosticsAsync(apiClient, translations, controller.signal));
  }

  const status = result?.status;
  const isHealthy = status === DiagnosticStatuses.Completed;
  const isFailed = status === DiagnosticStatuses.Failed || status === DiagnosticStatuses.TimedOut;
  const isPending = !error && status === DiagnosticStatuses.Pending;
  const isBusy = isRunning || isPending;
  const formattedTime = result ? formatTimestamp(result.updatedAt) : '';
  const userInfo = result?.userId ? ' by ' + result.userId : '';
  const latestRunText = result
    ? 'Latest diagnostic run' + userInfo + ' at ' + formattedTime
    : '';

  return (
    <div className="diagnostics-panel">
      <div className="diagnostics-status">
        <button
          type="button"
          className="diagnostics-run-button"
          title={translations.RunButton}
          aria-label={translations.RunButton}
          onClick={handleRun}
          disabled={isBusy}
        >
          <Components.Icon icon="sync" />
        </button>

        {isBusy && (
          <span className="diagnostics-text running">{translations.RunningStatus}</span>
        )}

        {!isBusy && isLoading && !result && (
          <span className="diagnostics-text running">{translations.LoadingStatus}</span>
        )}

        {!isBusy && error && (
          <span className="diagnostics-text failed">{translations.ErrorStatus}</span>
        )}

        {!isBusy && !error && isHealthy && (
          <span className="diagnostics-text healthy">{translations.HealthyStatus}</span>
        )}

        {!isBusy && !error && isFailed && (
          <span className="diagnostics-text failed">{translations.FailedStatus}</span>
        )}

        {!isBusy && !isLoading && !error && !result && (
          <span className="diagnostics-text empty">{translations.NotRunYetStatus}</span>
        )}
      </div>

      {!isBusy && error && (
        <p className="diagnostics-message">{error}</p>
      )}

      {!isBusy && !error && isFailed && result && (
        <p className="diagnostics-message">{getDiagnosticErrorMessage(result.errorCode, result.message)}</p>
      )}

      {!isBusy && !error && (isHealthy || isFailed) && result && (
        <p className="diagnostics-meta">{latestRunText}</p>
      )}
    </div>
  );
};
